// @need-translate
import {UrlLink} from '../../../applink/UrlLink';
import {getFormattedOver10Thousand} from '../../../common/shared/utils/getFormattedOver10Thousand';
import {twMerge} from '../../../common/twMerge';
import {useIsResponsiveFoldable} from '../../../common/useIsResponsiveFoldable.hook';
import {LOG_CLICK_EVENT} from '../../../logging/logEvent';
import {putTiaraObjectProps} from '../../../logging/putTiaraObjectProps';
import {theme} from '../../../theme';
import {Divider} from '../../atoms/Divider/Divider';
import {Icon} from '../../atoms/Icon/Icon';
import {IconType} from '../../atoms/Icon/IconType.enum';
import {List} from '../../atoms/List/List';
import {Text} from '../../atoms/Text/Text';
import {ViewerNavbarMenuProps} from './ViewerNavbarMenuProps.interface';

export function ViewerNavbarMenu({
  isShortsDrama,
  isMobile,
  seriesId,
  productId,
  commentCount,
  isNonstopWatching,
  hasViewTypeButton,
  isScrollView,
  hasListButton,
  isPrevButtonEnabled,
  isNextButtonEnabled,
  onClickViewTypeButton,
  onClickListButton,
  onClickNonStopWatchingButton,
  onClickCommentButton,
  onClickPrevButton,
  onClickNextButton,
  prevButtonTiaraObject,
  nextButtonTiaraObject,
  className,
  ...props
}: ViewerNavbarMenuProps) {
  const isFoldable = useIsResponsiveFoldable();
  return (
    <div
      className={twMerge(
        'flex w-full flex-row items-center justify-between',
        isMobile && 'px-15pxr',
        className
      )}
      style={{
        height: `${VIEWER_BOTTOM_MENU_HEIGHT_PX}px`,
      }}
      {...props}
    >
      <List gapPx={isMobile ? (isFoldable ? 84 : 56) : 24} className="mr-40pxr">
        {hasListButton && (
          <Icon
            className="cursor-pointer"
            type={IconType.ViewerBarList}
            onClick={onClickListButton}
          />
        )}
        {!isMobile && hasViewTypeButton && (
          <Icon
            className="cursor-pointer"
            type={
              // 현재 뷰어 상태를 보여주는 것이 아닌 클릭했을 때 바뀔 상태를 보여줌
              isScrollView
                ? IconType.NavigationPagePc
                : IconType.NavigationScrollPc
            }
            {...putTiaraObjectProps({
              click: {
                copy: isScrollView ? '페이지' : '스크롤',
              },
            })}
            onClick={e =>
              LOG_CLICK_EVENT('열람방식_클릭', e, onClickViewTypeButton)
            }
          />
        )}
        <Icon
          className={twMerge('cursor-pointer', isShortsDrama && 'hidden')}
          type={
            isNonstopWatching
              ? IconType.NavigationRunmodeOnStatic
              : IconType.NavigationRunmodeOff
          }
          {...putTiaraObjectProps({
            click: {
              copy: isNonstopWatching ? 'off' : 'on',
            },
          })}
          onClick={e =>
            LOG_CLICK_EVENT('정주행_클릭', e, onClickNonStopWatchingButton)
          }
        />
        <UrlLink
          className={twMerge(isShortsDrama && 'hidden')}
          url={
            seriesId !== undefined && productId !== undefined
              ? `/content/${seriesId}/viewer/${productId}/comment`
              : ''
          }
        >
          <div className="relative flex">
            <Icon
              className="cursor-pointer"
              type={IconType.NavigationComment}
              onClick={e =>
                LOG_CLICK_EVENT('회차댓글_클릭', e, onClickCommentButton)
              }
            />
            {
              // commentCount의 truthy 처리(commentCount &&)로 했을 때 이상동작을 하여 undefined 처리로 적용합니다.
              commentCount !== undefined && commentCount > 0 && (
                <div
                  className={twMerge(
                    'absolute left-14pxr -top-6pxr flex items-center justify-center rounded-16pxr bg-theme-solid-100 px-5pxr w-auto',
                    getFormattedOver10Thousand(commentCount).length === 1 &&
                      'h-16pxr w-16pxr'
                  )}
                >
                  <Text className="font-x-small2-bold shrink-0 whitespace-nowrap text-theme-solid-0">
                    {getFormattedOver10Thousand(commentCount)}
                  </Text>
                </div>
              )
            }
          </div>
        </UrlLink>
      </List>
      <div
        className="flex flex-row items-center"
        {...putTiaraObjectProps({
          customProps: {
            is_shortdrama: isShortsDrama ? 'y' : undefined,
          },
        })}
      >
        {isMobile && (
          <>
            <Icon
              type={IconType.NavigationArrowLeft}
              {...prevButtonTiaraObject}
              onClick={
                isPrevButtonEnabled
                  ? e => LOG_CLICK_EVENT('이전편_클릭', e, onClickPrevButton)
                  : () => {}
              }
              className={twMerge(
                'mr-56pxr flex cursor-pointer flex-row items-center foldable:mr-84pxr',
                !isPrevButtonEnabled && 'cursor-default opacity-30'
              )}
            />
            <Icon
              data-test="viewer-navbar-next-button"
              type={IconType.NavigationArrowRight}
              {...nextButtonTiaraObject}
              onClick={
                isNextButtonEnabled
                  ? e => LOG_CLICK_EVENT('다음편_클릭', e, onClickNextButton)
                  : () => {}
              }
              className={twMerge(
                'flex cursor-pointer flex-row items-center',
                !isNextButtonEnabled && 'cursor-default opacity-30'
              )}
            />
          </>
        )}
        {!isMobile && (
          <>
            <div
              className={twMerge(
                'flex cursor-pointer flex-row items-center',
                !isPrevButtonEnabled && 'cursor-default opacity-30'
              )}
              {...prevButtonTiaraObject}
              onClick={
                isPrevButtonEnabled
                  ? e => LOG_CLICK_EVENT('이전편_클릭', e, onClickPrevButton)
                  : () => {}
              }
            >
              <Icon
                type={IconType.NavigationArrowLeft}
                className="pointer-events-none"
              />
              <Text className="font-medium2-bold ml-4pxr text-theme-solid-100">
                이전화
              </Text>
            </div>
            <Divider
              direction="vertical"
              sizePx={1}
              color={theme.colors['theme-transparent-20']}
              className="mx-16pxr h-14pxr"
            />
            <div
              data-test="viewer-navbar-next-button"
              className={twMerge([
                'flex cursor-pointer flex-row items-center',
                !isNextButtonEnabled && 'cursor-default opacity-30',
              ])}
              {...nextButtonTiaraObject}
              onClick={
                isNextButtonEnabled
                  ? e => LOG_CLICK_EVENT('다음편_클릭', e, onClickNextButton)
                  : () => {}
              }
            >
              <Text className="font-medium2-bold mr-4pxr text-theme-solid-100">
                다음화
              </Text>
              <Icon
                type={IconType.NavigationArrowRight}
                className="pointer-events-none"
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const VIEWER_BOTTOM_MENU_HEIGHT_PX = 56;
